import { format } from 'date-fns';

import type {
  MatterSummary,
  NoteContext,
  TaskContext,
} from '@/services/api/notificationsService';
import {
  useClientConversationDetailQuery,
  useNotificationContextQuery,
} from '@/services/api/notificationsService';

import type { NotificationNavTab } from './NotificationNav';

interface CaseDetailsPanelProps {
  selectedId: string | null;
  tab: NotificationNavTab;
}

interface InfoRowProps {
  label: string;
  value: string | null | undefined;
}

const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => {
  if (!value) return null;

  return (
    <div className="flex justify-between items-start gap-2 py-1.5">
      <span className="text-[11px] text-gray-500 font-medium">{label}</span>
      <span className="text-[11px] text-gray-900 font-semibold text-right truncate max-w-[140px]">{value}</span>
    </div>
  );
};

/**
 * Format a date string to a readable format
 */
const formatDate = (dateString: string | null | undefined): string | null => {
  if (!dateString) return null;
  try {
    const date = new Date(dateString);
    return format(date, 'MMM d, yyyy');
  } catch {
    return dateString;
  }
};

interface MatterSectionProps {
  matter: MatterSummary;
}

const MatterSection: React.FC<MatterSectionProps> = ({ matter }) => {
  return (
    <div className="mb-4">
      <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">
        Case
      </h3>
      <div>
        <InfoRow label="Matter" value={matter.name} />
        <InfoRow label="Type" value={matter.incident_type} />
        <InfoRow label="Date" value={formatDate(matter.incident_date)} />
        <InfoRow label="Attorney" value={matter.lead_attorney} />
        <InfoRow label="Status" value={matter.status} />
      </div>
    </div>
  );
};

interface TaskSectionProps {
  task: TaskContext;
}

const TaskSection: React.FC<TaskSectionProps> = ({ task }) => {
  return (
    <div className="mb-4">
      <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">
        Task
      </h3>
      <div>
        <InfoRow label="Title" value={task.title} />
        <InfoRow label="Status" value={task.status} />
        <InfoRow label="Assignee" value={task.assigned_to} />
        <InfoRow label="Due" value={formatDate(task.due_date)} />
        <InfoRow label="Priority" value={task.priority} />
      </div>
    </div>
  );
};

interface NoteSectionProps {
  note: NoteContext;
}

const NoteSection: React.FC<NoteSectionProps> = ({ note }) => {
  return (
    <div className="mb-4">
      <h3 className="text-[10px] font-bold text-gray-400 uppercase tracking-wide mb-2">
        Note
      </h3>
      <div>
        <InfoRow label="Title" value={note.title} />
        <InfoRow label="Author" value={note.author?.display_name} />
        <InfoRow label="Created" value={formatDate(note.created_at)} />
      </div>
    </div>
  );
};

export const CaseDetailsPanel: React.FC<CaseDetailsPanelProps> = ({
  selectedId,
  tab,
}) => {
  const isMessagesTab = tab === 'messages';

  // Fetch notification context for mentions/updates tabs
  const { data: notificationContext, isLoading: notificationLoading } = useNotificationContextQuery(
    { id: selectedId! },
    { skip: !selectedId || isMessagesTab },
  );

  // Fetch conversation detail for messages tab (includes matter details)
  const { data: conversationDetail, isLoading: conversationLoading } = useClientConversationDetailQuery(
    { matterId: selectedId! },
    { skip: !selectedId || !isMessagesTab },
  );

  const isLoading = isMessagesTab ? conversationLoading : notificationLoading;

  // Loading state
  if (isLoading) {
    return (
      <div className="h-full border-l border-gray-200 bg-gray-50 px-4 py-3 flex items-center justify-center">
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-indigo-600 border-t-transparent" />
      </div>
    );
  }

  // For Messages tab, get matter from conversation detail
  if (isMessagesTab) {
    const matter = conversationDetail?.matter;

    if (!matter) {
      return (
        <div className="h-full border-l border-gray-200 bg-gray-50 px-4 py-3">
          <p className="text-xs text-gray-500 font-medium">No details</p>
        </div>
      );
    }

    return (
      <div className="h-full border-l border-gray-200 bg-gray-50 px-4 py-3 overflow-y-auto">
        <MatterSection matter={matter} />
      </div>
    );
  }

  // For mentions/updates tabs, use notification context
  if (!notificationContext) {
    return (
      <div className="h-full border-l border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-xs text-gray-500 font-medium">No details</p>
      </div>
    );
  }

  const { matter, task, note } = notificationContext;

  // No matter context
  if (!matter && !task && !note) {
    return (
      <div className="h-full border-l border-gray-200 bg-gray-50 px-4 py-3">
        <p className="text-xs text-gray-500 font-medium">No context</p>
      </div>
    );
  }

  return (
    <div className="h-full border-l border-gray-200 bg-gray-50 px-4 py-3 overflow-y-auto">
      {matter && <MatterSection matter={matter} />}
      {task && <TaskSection task={task} />}
      {note && <NoteSection note={note} />}
    </div>
  );
};
